import { makeStyles, tokens, ToolbarButton, Tooltip } from '@fluentui/react-components'
import { ArrowUndoRegular, HistoryRegular } from '@fluentui/react-icons'
import { useTranslation } from 'react-i18next'
import { Dialog } from '../Dialog/Dialog'
import { TableEditorProvider } from './TableEditorContext'
import { TableFiltering } from './TableFiltering'
import { useTableEditorContext } from './useTableEditorContext'

interface TableFilterModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  fileId: string
  sectionId: string
}

const useStyles = makeStyles({
  titleActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  // Tables need more horizontal space than a typical dialog.
  // marginLeft/Right: auto restores the position:fixed;inset:0;margin:auto centering
  // that Fluent UI uses by default — the base `dialog` class overrides those margins
  // with a fixed pixel value, which shifts the surface to the left at narrow viewports.
  wideDialog: {
    width: '100%',
    maxWidth: '90vw',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
})

/**
 * Modal wrapper for TableFiltering.
 * Opened from FileCard (three-dot menu or double-click) → Files.page tableModal state.
 */
const TableFilterModal = ({ open, onOpenChange, fileId, sectionId }: TableFilterModalProps) => (
  <TableEditorProvider fileId={fileId} sectionId={sectionId}>
    <TableFilterModalContent open={open} onOpenChange={onOpenChange} />
  </TableEditorProvider>
)

const TableFilterModalContent = ({
  open,
  onOpenChange,
}: Pick<TableFilterModalProps, 'open' | 'onOpenChange'>) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const {
    handleUndo,
    handleResetTable,
    insertFilteredTableToDocument,
    canUndo,
    canReset,
    isTableDataLoading,
    isTableDataError,
  } = useTableEditorContext()

  return (
    <Dialog
      title={t('tables.filterModalTitle')}
      customDialogTitleActions={
        <div className={classes.titleActions}>
          <Tooltip content={t('tables.undo')} relationship="label">
            <ToolbarButton
              icon={<ArrowUndoRegular />}
              onClick={handleUndo}
              disabled={!canUndo}
              aria-label={t('tables.undo')}
            />
          </Tooltip>
          <Tooltip content={t('tables.resetTable')} relationship="label">
            <ToolbarButton
              icon={<HistoryRegular />}
              onClick={handleResetTable}
              disabled={!canReset}
              aria-label={t('tables.resetTable')}
            />
          </Tooltip>
        </div>
      }
      content={<TableFiltering />}
      surfaceClassName={classes.wideDialog}
      isError={isTableDataError}
      errorMessage={t('tables.loadError')}
      onOpenChange={() => onOpenChange(!open)}
      isDialogOpen={open}
      primaryButtonText={t('tables.insertTableAtCursor')}
      onConfirm={insertFilteredTableToDocument}
      isLoading={isTableDataLoading}
    />
  )
}

export { TableFilterModal }
